import {
    Close,
    Root,
    Trigger,
    Overlay,
    Content,
    Title,
    Description,
    Portal,
} from '@radix-ui/react-dialog'

import React, { ReactNode } from 'react'
import { createUseStyles, useTheme } from 'react-jss'
import { colors } from '../../../theme/palette'
import { Close as CloseIcon } from '../../../assets/icons'
import { dialogCloseButton } from '../../../theme/reuseables'

interface StyleProps {
    iconBGColor?: string
    iconBorder?: string
}

type RuleNames =
    | 'overlayContainer'
    | 'container'
    | 'overlay'
    | 'button'
    | 'circleIcon'
    | '@keyframes animatetop'
    | '@keyframes fadeIn'

export const useStyles = createUseStyles<RuleNames, StyleProps, Jss.Theme>({
    // const useStyles = createUseStyles({
    '@keyframes animatetop': {
        from: {
            top: '20px',
            opacity: 0,
        },
        to: {
            top: '50%',
            opacity: 1,
        },
    },
    '@keyframes fadeIn': {
        from: {
            opacity: 0,
        },
        to: {
            opacity: 1,
        },
    },
    overlayContainer: {
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100%',
        height: '100svh',
        zIndex: 6,
    },
    container: {
        backgroundColor: 'white',
        borderRadius: '6px',
        boxShadow:
            // eslint-disable-next-line max-len
            'rgba(14, 18, 22, 0.35) 0px 10px 38px -10px, rgba(14, 18, 22, 0.2) 0px 10px 20px -15px',
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90vw',
        maxWidth: '500px',
        maxHeight: '85vh',
        padding: '2.75rem',
        animation: '.6s $animatetop',
        AnimationEffect: 'easeInOut',
        pointerEvents: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        zIndex: 5,
        '&.withIcon': {
            paddingTop: '80px',
        },
        '&.large': {
            maxWidth: '800px',
        },
    },
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        position: 'fixed',
        inset: '0px',
        animation: '.3s $fadeIn',
        zIndex: 4,
    },
    button: {
        ...dialogCloseButton,
    },
    circleIcon: {
        position: 'absolute',
        top: '-40px',
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        backgroundColor: ({ iconBGColor }) => {
            return iconBGColor || colors.blue300
        },
        border: ({ iconBorder }) => {
            return `2px solid ${iconBorder || colors.white}`
        },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '1rem',
    },
})

const DialogTrigger: React.FC = ({ children }) => {
    return <Trigger>{children}</Trigger>
}

type DialogProps = {
    title?: string
    description?: string
    trigger?: ReactNode
    defaultOpen?: boolean
    hideClose?: boolean
    icon?: ReactNode
    iconBGColor?: string
    iconBorder?: string
    hideOverlay?: boolean
}

const defaultProps = {
    title: '',
    description: '',
    trigger: <>Trigger</>,
    defaultOpen: false,
    hideClose: false,
    icon: undefined,
    iconBGColor: colors.blue300,
    iconBorder: colors.white,
    hideOverlay: false,
}

const Dialog: React.FC<DialogProps> = ({
    children,
    title,
    description,
    trigger,
    defaultOpen,
    hideClose,
    icon,
    iconBGColor,
    iconBorder,
    hideOverlay,
}) => {
    const handleIsOpen = (): void => {
        const documentWidth = document.documentElement.clientWidth
        const windowWidth = window.innerWidth
        const scrollBarWidth = windowWidth - documentWidth

        document.body.style.overflow = 'hidden'
        document.body.style.paddingRight = `${scrollBarWidth}px`
        document.body.classList.add('no-scroll')
    }

    const handleIsClosed = (): void => {
        document.body.style.overflow = 'unset'
        document.body.style.paddingRight = 'unset'
        document.body.classList.remove('no-scroll')
    }

    const theme = useTheme<Jss.Theme>()
    const classes = useStyles({
        ...{ iconBGColor, iconBorder },
        theme,
    })

    return (
        <Root
            defaultOpen={!!defaultOpen}
            onOpenChange={(open) => (open ? handleIsOpen() : handleIsClosed())}
        >
            <Trigger asChild>{trigger}</Trigger>
            <Portal>
                {!hideOverlay ? <Overlay className={classes.overlay} /> : null}
                <Content
                    className={`${classes.overlayContainer} ${
                        icon ? 'withIcon' : ''
                    }`}
                >
                    <div
                        className={`${classes.container} ${
                            icon ? 'withIcon' : ''
                        }`}
                    >
                        {icon ? (
                            <div className={classes.circleIcon}>{icon}</div>
                        ) : null}
                        <Title>{title}</Title>
                        <Description>{description}</Description>
                        {!hideClose ? (
                            <Close className={classes.button}>
                                <CloseIcon width="32px" />
                            </Close>
                        ) : null}
                        {children}
                    </div>
                </Content>
            </Portal>
        </Root>
    )
}

Dialog.defaultProps = defaultProps

export { Dialog, DialogTrigger }
